import { useCallback, useEffect, useRef } from "react";
import { Flex, FocusableElement, Text } from "../../..";
import { DialogCancelButton } from "../../../components/Dialog/DialogCancelButton";
import styles from "./OptionsDialog.module.scss";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import { DialogContext } from "../../../components/Dialog/internal/DialogContext";
import { useDialogScrollLock } from "../..";
import { tabbable } from "tabbable";

export const OptionsDialog = ({
  id,
  isOpen,
  onClose,
  field,
  children,
  title,
  initialFocusResolver,
}: {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  field?: React.ReactNode;
  children: React.ReactNode;
  initialFocusResolver?: (focusables: FocusableElement[]) => FocusableElement;
}) => {
  const elRef = useRef<HTMLDialogElement>(null);
  const visualViewportHeightRef = useRef<HTMLDivElement>(null);

  useDialogScrollLock({
    open: isOpen,
    id,
    enableScrollChaining: false,
    elementRef: elRef,
  });

  const computeFocus = useCallback(
    (focusables: FocusableElement[]) => {
      if (initialFocusResolver) {
        return initialFocusResolver(focusables);
      }
      return (
        focusables.find(
          (focusable) => focusable.dataset.anv !== "dialog-cancel-button",
        ) || focusables[0]
      );
    },
    [initialFocusResolver],
  );

  useEffect(() => {
    if (isOpen) {
      elRef.current?.showModal();
      if (elRef.current) {
        computeFocus(tabbable(elRef.current)).focus();
      }
    } else {
      elRef.current?.close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Omitting computeFocus because we don't want to show/close the dialog on computeFocus change
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const handleResize = () => {
        if (!visualViewportHeightRef.current) return;
        visualViewportHeightRef.current.style.height = `${window.visualViewport?.height ?? 0}px`;
        visualViewportHeightRef.current.style.maxHeight = `${window.visualViewport?.height ?? 0}px`;
      };
      window.visualViewport?.addEventListener("resize", handleResize);
      return () => {
        window.visualViewport?.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen]);

  return (
    <dialog
      ref={elRef}
      id={id}
      data-anv="field-dialog"
      className={styles["field-dialog"]}
      onKeyDown={(e) => {
        if (e.code === "Escape") {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div
        className={styles["field-dialog-visual-viewport-height"]}
        ref={visualViewportHeightRef}
      >
        <Flex direction="row" className={styles["header"]}>
          <Text variant="headline" el="h1">
            {title}
          </Text>
          <span className={styles["close-container"]}>
            <DialogContext.Provider value={{ close: onClose }}>
              <DialogCancelButton
                className={styles["close-button"]}
                size="small"
                appearance="ghost"
                aria-label="Close dialog"
                icon={Close}
              />
            </DialogContext.Provider>
          </span>
        </Flex>
        {field ? (
          <Flex direction="column" className={styles["field"]}>
            {field}
          </Flex>
        ) : null}
        <Flex direction="column" className={styles["content"]}>
          {children}
        </Flex>
      </div>
    </dialog>
  );
};
